import { defineComponent as _defineComponent } from 'vue'
import { PropType } from 'vue'
import { AxiosResponse } from 'axios'
import { ref, onMounted, computed, useContext } from '@nuxtjs/composition-api'
import type { IGetDataResponse } from '@entities/discount/model/types'
import { useOrderModel } from '@entities/order'
import { useCustomLogicModel } from '@shared/model'
import { usePreloaderModel } from '@shared/model/preloader'
import { useRestaurantModel } from '@entities/restaurant'
import { useOrderModelWidget } from '@widgets/order'
import { useDiffCheckModel } from '@features/diff-check'
import { AppButton } from '@shared/ui'
import { getOS } from '@shared/utils/getOS'
import { discountGetData, discountSetPromoCode } from '@entities/discount'
import { calcSumAlienSelectedElements } from '@entities/order-item'
import { useTipsModel } from '@entities/tips'
import { useMetaModel } from '@entities/meta'
import { useLoyaltyModel, BonusButton } from '~/src/entities/loyalty'

interface IError {
  code?: number
}
interface IErrorsPromocode {
  errors?: IError
}


export default /*#__PURE__*/_defineComponent({
  __name: 'OrderTotal',
  props: {
  disabledWaitingDiscount: {
    type: Boolean as PropType<boolean>,
    required: true,
    default: false
  },
  waitingAcceptPromocode: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false
  },
  acceptingPromocode: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false
  },
  separateOrderAlienGuest: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false
  },
  discount: {
    type: String as PropType<string>,
    required: false,
    default: '0'
  },
  currencyIcon: {
    type: String as PropType<string>,
    required: false,
    default: ''
  },
  selectedSum: {
    type: String as PropType<string>,
    required: false,
    default: '0'
  },
  tipsVisible: {
    type: Boolean as PropType<boolean>,
    required: false,
    default: false
  },
  totalSum: {
    type: String as PropType<string>,
    required: false,
    default: '0'
  }
},
  emits: [
  'toggleApplyDiscount',
  'onDiscountProcess',
  'onDisabledWaitingDiscount',
  'changeActiveModalBonus',
  'toggleHasDiscount',
  'togglePromocodeError',
  'toggleWaitingAcceptPromocode',
  'toggleWaitingPromocode',
  'confirmPromocode'
],
  setup(__props, { emit }) {

const props = __props

const { $cookies } = useContext()

const orderModel = useOrderModel()
const customLogicModel = useCustomLogicModel()
const preloaderModel = usePreloaderModel()
const restaurantModel = useRestaurantModel()
const orderModelWidget = useOrderModelWidget()
const diffCheckModel = useDiffCheckModel()
const tipsModel = useTipsModel()
const metaModel = useMetaModel()
const loyaltyModel = useLoyaltyModel()





const errorPromoCode = ref<string>('')
const userIos = ref<boolean>(false)
const hidePrimehillButton = ref<boolean>(false)

// Computed
const buttonDiscountIsShow = computed(() => {
  if (orderModel.orderData) {
    return userIos.value && orderModel.orderData.discount === 0 && orderModel.orderData?.loyalty_system !== 'OFF' && orderModel.orderData.loyalty_system === 'NOVIKOV' && !props.disabledWaitingDiscount
  }
  return false
})

const isBonusButton = computed(() => {
  if (orderModel.orderData) {
    return (orderModel.orderData.global_system_loyalty?.code === 'iiko_card' || orderModel.orderData.global_system_loyalty?.code === 'prime_hill') && !(orderModel.orderData.global_system_loyalty?.application_loyalty && loyaltyModel.sumDiscountPrimeHill && !orderModel.orderData.bonus) && !hidePrimehillButton.value
  }
  return false
})

// Выбранные позиции другим пользователем
const alienSelectedSum = computed(() => {
  return parseFloat(calcSumAlienSelectedElements(
    orderModel.orderElements,
    metaModel.guest ? Number(metaModel.guest) : 0
  ).toFixed(2))
})

const sumTotalSum = computed(() => {
  const newTips = props.tipsVisible ? tipsModel.tipsSum : 0
  return (parseFloat(props.totalSum) + newTips).toFixed(2)
})

// Methods
const openApplyDiscount = (data: AxiosResponse<IGetDataResponse>) => {
  if (data.data.result.code === 106) {
    emit('toggleApplyDiscount')
  } else {
    emit('onDiscountProcess')
    emit('onDisabledWaitingDiscount')
    setTimeout(() => {
      emit('onDiscountProcess')
    }, 3000)
  }
}

const clearError = () => {
  errorPromoCode.value = ''
}

const togglePrimeHillButton = () => {
  hidePrimehillButton.value = !hidePrimehillButton.value
}

const changeActiveModalBonus = (val: string) => {
  emit('changeActiveModalBonus', val)
}

const toggleHasPrimeHillDiscount = () => {
  emit('toggleHasDiscount')
  setTimeout(() => {
    emit('toggleHasDiscount')
  }, 3000)
}

const toggleApplyDiscount = () => {
  const sessionsId = parseInt(orderModel.orderData?.session ? orderModel.orderData?.session : '')
  discountGetData({ sessions_id: sessionsId })
    .then(function (res) {
      openApplyDiscount(res)
    })
    .catch(function (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    })
}

const isErrorPromocode = (data: IErrorsPromocode) => {
  preloaderModel.status = !preloaderModel.status
  if (data?.errors?.code === 404) {
    errorPromoCode.value = 'Промокод недействителен'
  } else {
    emit('togglePromocodeError')
    setTimeout(() => {
      emit('togglePromocodeError')
    }, 3000)
  }
}

const isPromocodeAccepted = (data: AxiosResponse) => {
  if (data.status === 201) {
    preloaderModel.status = !preloaderModel.status
    emit('toggleWaitingAcceptPromocode')
    emit('toggleWaitingPromocode')
    setTimeout(() => {
      emit('toggleWaitingPromocode')
    }, 3000)
  } else if (data.status === 200) {
    emit('confirmPromocode')
  }
}

const applyPromoCode = async (promoCode: string) => {
  if (props.discount !== '0') {
    emit('toggleHasDiscount')
    setTimeout(() => {
      emit('toggleHasDiscount')
    }, 3000)
  } else {
    preloaderModel.status = !preloaderModel.status
    const payload = {
      shop_code: restaurantModel.code,
      order_guid: orderModel.orderData ? orderModel.orderData.orders[0].order_id : '',
      code: promoCode
    }

    try {
      const res = await discountSetPromoCode(payload)
      const orderId = orderModel.orderData ? orderModel.orderData.orders[0].order_id : '0'
      $cookies.set('promocode', promoCode, {
        maxAge: 60 * 60 * 6 * 1
      })
      $cookies.set('order_id', orderId, {
        maxAge: 60 * 60 * 6 * 1
      })
      isPromocodeAccepted(res)
    } catch (err: any) {
      isErrorPromocode(err.response.data)
    }
  }
}

onMounted(() => {
  userIos.value = getOS() === 'ios'
})

return { __sfc: true,$cookies, orderModel, customLogicModel, preloaderModel, restaurantModel, orderModelWidget, diffCheckModel, tipsModel, metaModel, loyaltyModel, props, emit, errorPromoCode, userIos, hidePrimehillButton, buttonDiscountIsShow, isBonusButton, alienSelectedSum, sumTotalSum, openApplyDiscount, clearError, togglePrimeHillButton, changeActiveModalBonus, toggleHasPrimeHillDiscount, toggleApplyDiscount, isErrorPromocode, isPromocodeAccepted, applyPromoCode, AppButton, BonusButton }
}

})